import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { BACKEND_URL } from "@/lib/api";

const CURRENCIES = [
  { code: "GBP", symbol: "\u00a3", name: "British Pound Sterling", locale: "en-GB" },
  { code: "USD", symbol: "$", name: "US Dollar", locale: "en-US" },
  { code: "EUR", symbol: "\u20ac", name: "Euro", locale: "de-DE" },
  { code: "AED", symbol: "AED", name: "UAE Dirham", locale: "ar-AE" },
  { code: "CHF", symbol: "CHF", name: "Swiss Franc", locale: "de-CH" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar", locale: "en-AU" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar", locale: "en-CA" },
  { code: "SAR", symbol: "SAR", name: "Saudi Riyal", locale: "ar-SA" },
  { code: "QAR", symbol: "QAR", name: "Qatari Riyal", locale: "ar-QA" },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar", locale: "en-SG" },
  { code: "INR", symbol: "\u20b9", name: "Indian Rupee", locale: "en-IN" },
  { code: "JPY", symbol: "\u00a5", name: "Japanese Yen", locale: "ja-JP" },
  { code: "HKD", symbol: "HK$", name: "Hong Kong Dollar", locale: "en-HK" },
  { code: "ZAR", symbol: "R", name: "South African Rand", locale: "en-ZA" },
  { code: "SEK", symbol: "kr", name: "Swedish Krona", locale: "sv-SE" },
  { code: "NOK", symbol: "kr", name: "Norwegian Krone", locale: "nb-NO" },
  { code: "DKK", symbol: "kr", name: "Danish Krone", locale: "da-DK" },
];

const DEFAULT_CURRENCY = CURRENCIES[0]; // GBP

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    // Restore from localStorage cache to avoid flash
    const cached = localStorage.getItem("mep_currency_code");
    if (cached) {
      const found = CURRENCIES.find((c) => c.code === cached);
      if (found) return found;
    }
    return DEFAULT_CURRENCY;
  });

  // Fetch currency from the PUBLIC endpoint (no auth needed) — once on mount
  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/settings/public/currency`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const code = data?.currency_code;
        if (code) {
          const found = CURRENCIES.find((c) => c.code === code);
          if (found) {
            setCurrencyState(found);
            localStorage.setItem("mep_currency_code", code);
          }
        }
      })
      .catch(() => {}); // Use localStorage cache as fallback
    return () => { cancelled = true; };
  }, []);

  // refreshCurrency can still be called after admin changes currency in settings
  const refreshCurrency = useCallback(() => {
    fetch(`${BACKEND_URL}/api/settings/public/currency`)
      .then((r) => r.json())
      .then((data) => {
        const code = data?.currency_code;
        if (code) {
          const found = CURRENCIES.find((c) => c.code === code);
          if (found) {
            setCurrencyState(found);
            localStorage.setItem("mep_currency_code", code);
          }
        }
      })
      .catch(() => {});
  }, []);

  const format = (value, opts = {}) => {
    const num = Number(value) || 0;
    const decimals = opts.decimals !== undefined ? opts.decimals : 2;
    const formatted = num.toLocaleString("en-GB", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return `${currency.symbol}${formatted}`;
  };

  const formatCompact = (value) => {
    const num = Number(value) || 0;
    if (num >= 1000000) return `${currency.symbol}${(num / 1000000).toFixed(1)}m`;
    if (num >= 1000) return `${currency.symbol}${(num / 1000).toFixed(0)}k`;
    return `${currency.symbol}${num.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
  };

  const ctx = {
    ...currency,
    currencies: CURRENCIES,
    format,
    formatCompact,
    refreshCurrency,
    setCurrency: (code) => {
      const found = CURRENCIES.find((c) => c.code === code);
      if (found) {
        setCurrencyState(found);
        localStorage.setItem("mep_currency_code", code);
      }
    },
  };

  return <CurrencyContext.Provider value={ctx}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) return { symbol: "\u00a3", code: "GBP", name: "British Pound Sterling", locale: "en-GB", currencies: CURRENCIES, format: (v) => `\u00a3${Number(v||0).toLocaleString("en-GB", {minimumFractionDigits:2})}`, formatCompact: (v) => `\u00a3${Number(v||0).toLocaleString()}`, setCurrency: () => {}, refreshCurrency: () => {} };
  return ctx;
}

export { CURRENCIES };
